import type { CSSProperties } from 'react';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import './ApplicationTestingTickets.scss';

/**
 * Application Testing statuses and priorities - the values a ticket can take, how each is named and coloured, and
 * the editable pills that show them. Shared by the ticket list, the ticket page, the dashboard and Create Ticket.
 */
/** Ids are 10-22, not 0-7: see the backend's db/schema/48_application_testing_status_v2.sql and
 *  51_application_testing_status_v3.sql - 10-17 were kept outside the old 0-5 range so every stored ticket could be
 *  remapped without an id ever meaning two things, and 18-22 are the later additions. Order is the order of the
 *  workflow as the pickers, filters and dashboard list it. */
export const statusOptions = [
  { id: 12, name: 'Open' },
  { id: 18, name: 'Requirement Pending' },
  { id: 19, name: 'Blocked' },
  { id: 20, name: 'Clarification Required' },
  { id: 21, name: 'Invalid' },
  { id: 14, name: 'In Progress' },
  { id: 22, name: 'Ready for BA Review' },
  { id: 17, name: 'Closed' },
  { id: 16, name: 'Reopened' },
];

/** The status lists are wider than the pill that opens them, so "Clarification Required" is not cut short. */
export const STATUS_PANEL_MIN_WIDTH = 240;

/** What each status means, shown in the status lists and on the ticket page. */
export const STATUS_DESCRIPTION: Record<number, string> = {
  12: 'Ticket is logged and ready for review or assignment.',
  18: 'BA or Product Owner must clarify requirements, acceptance criteria, or expected behaviour.',
  19: 'Work cannot continue because of a dependency, access issue, environment issue, or external team.',
  20: 'A specific question or decision is pending from the BA, developer, or stakeholder.',
  21: 'Ticket is not valid, is out of scope, is a duplicate, or is based on incorrect information. Requires a reason.',
  14: 'Developer is actively working on the ticket.',
  22: 'Developer has completed the work and BA needs to validate it.',
  17: 'Work has been verified and accepted.',
  16: 'Previously closed ticket needs additional work or correction.',
};

/** Statuses no longer offered. A ticket still in one keeps showing it (in its own list only) until it is moved on. */
const RETIRED_STATUSES: Record<number, string> = {
  10: 'Enhancement',
  11: 'Requirement',
  13: 'Hold',
  15: 'QA',
};

/** The statuses a ticket can be moved to, plus its own retired status (if any) so its current value still shows. */
export function statusChoicesFor(current: number | null | undefined): { id: number; name: string }[] {
  if (current == null || statusOptions.some((s) => s.id === current)) return statusOptions;
  return [{ id: current, name: RETIRED_STATUSES[current] ?? 'Unknown' }, ...statusOptions];
}

const STATUS_COLOR: Record<number, string> = {
  10: '#8B5CF6', // Enhancement (retired) - purple
  11: '#3B82F6', // Requirement (retired) - blue
  12: '#F59E0B', // Open - amber
  13: '#F97316', // Hold (retired) - orange
  14: '#06B6D4', // In Progress - cyan
  15: '#6366F1', // QA (retired) - indigo
  16: '#F43F5E', // Reopened - rose
  17: '#22C55E', // Closed - green
  18: '#3B82F6', // Requirement Pending - blue
  19: '#DC2626', // Blocked - red
  20: '#8B5CF6', // Clarification Required - purple
  21: '#C2410C', // Invalid - burnt orange
  22: '#6366F1', // Ready for BA Review - indigo
};

export const statusColor = (id: number): string => STATUS_COLOR[id] ?? '#64748B';

export function renderStatusLabel(item: { id: number; name: string | null }) {
  const color = STATUS_COLOR[item.id];
  return <span style={{ fontWeight: 700, ...(color && { color }) }}>{item.name ?? ''}</span>;
}

/** High, Medium, Low - that order. "Extreme" (id 2) was retired from the picker; a ticket that still carries it
 *  (raised before the retirement) keeps rendering fine, just via PRIORITY_COLOR_CLASS's fallback, not this list. */
export const priorityOptions = [
  { id: 1, name: 'High' },
  { id: 3, name: 'Medium' },
  { id: 0, name: 'Low' },
];

/** Priorities no longer offered, and the offered priority each one counts as in totals (Extreme is at least High). */
const RETIRED_PRIORITIES: Record<number, { name: string; countsAs: number }> = {
  2: { name: 'Extreme', countsAs: 1 },
};

/** The offered priority a ticket counts under, or null when its priority is not one we know (Not Set). */
export function priorityGroupOf(priorityId: number): number | null {
  if (priorityOptions.some((p) => p.id === priorityId)) return priorityId;
  return RETIRED_PRIORITIES[priorityId]?.countsAs ?? null;
}

/** High red, Medium amber, Low green - "Extreme" keeps the strongest red if an old ticket still has it. */
const PRIORITY_COLOR_CLASS: Record<number, string> = {
  1: 'text-danger-100',
  3: 'text-yellow-100',
  0: 'text-success',
  2: 'text-danger-300',
};

const PRIORITY_COLOR: Record<number, string> = { 1: '#D92D20', 3: '#D97706', 0: '#16A34A', 2: '#B42318' };

export const priorityColor = (id: number): string => PRIORITY_COLOR[id] ?? '#64748B';

export function renderPriorityLabel(item: { id: number; name: string | null }) {
  return (
    <span className={`fw-bold ${PRIORITY_COLOR_CLASS[item.id] ?? ''}`.trim()}>
      {item.name ?? (item.id === 2 ? 'Extreme' : '')}
    </span>
  );
}

/** A status shown as a tinted pill: a coloured dot and the name. */
function statusPillLabel(item: { id: number; name: string | null }) {
  return (
    <span className="application-testing-pill-label">
      <span className="application-testing-pill-dot" />
      {item.name ?? ''}
    </span>
  );
}

/** A priority shown as a tinted pill: a flag and the name. */
function priorityPillLabel(item: { id: number; name: string | null }) {
  return (
    <span className="application-testing-pill-label">
      <Icon name="flag" />
      {item.name ?? (item.id === 2 ? 'Extreme' : '')}
    </span>
  );
}

const pillStyle = (color: string) => ({ '--pill-color': color }) as CSSProperties;

/** The status of a ticket as an editable pill (list and dashboard). Clicks stay inside, so a row click doesn't open it. */
export function StatusPill({
  value,
  disabled,
  onChange,
}: {
  value: number;
  disabled?: boolean;
  onChange: (status: number) => void;
}) {
  return (
    <div
      className="application-testing-pill"
      style={pillStyle(statusColor(value))}
      onClick={(e) => e.stopPropagation()}
    >
      <Select
        items={statusChoicesFor(value)}
        bindLabel="name"
        bindValue="id"
        value={value}
        clearable={false}
        searchable={false}
        disabled={disabled}
        onChange={(next) => onChange(next as number)}
        openOnFocus
        panelClass="myPanelClass application-testing-status-panel"
        panelMinWidth={STATUS_PANEL_MIN_WIDTH}
        renderLabel={statusPillLabel}
        renderOption={renderStatusLabel}
      />
    </div>
  );
}

/** The priority of a ticket as an editable pill (list and dashboard). */
export function PriorityPill({
  value,
  disabled,
  onChange,
}: {
  value: number;
  disabled?: boolean;
  onChange: (priority: number) => void;
}) {
  return (
    <div
      className="application-testing-pill priority"
      style={pillStyle(priorityColor(value))}
      onClick={(e) => e.stopPropagation()}
    >
      <Select
        items={priorityOptions}
        bindLabel="name"
        bindValue="id"
        value={value}
        clearable={false}
        searchable={false}
        disabled={disabled}
        onChange={(next) => onChange(next as number)}
        openOnFocus
        panelClass="myPanelClass"
        renderLabel={priorityPillLabel}
        renderOption={renderPriorityLabel}
      />
    </div>
  );
}
